import { Box, HStack, Pressable, Text, VStack } from '@gluestack-ui/themed';
import { CheckIcon } from 'lucide-react-native';
import { useContext } from 'react';
import { TranslationContext } from '../../contexts/translations/Translation';
import { COLORS } from '../../theme/colors';

const ORGANISMS = [
  { key: 'bacteria', letter: 'B', color: '#2A464D' },
  { key: 'yeast', letter: 'L', color: '#7FC9AF' },
  { key: 'mold', letter: 'M', color: '#C7E7F1' },
];

interface IOrganismSelectionCardsProps {
  selected: Set<string>;
  onToggle: (key: string) => void;
  coloration?: string;
  testID?: string;
}

const GRAM_ONLY_ORGANISMS = new Set(['yeast', 'mold']);

export const OrganismSelectionCards = ({ selected, onToggle, coloration, testID }: IOrganismSelectionCardsProps) => {
  const { i18n } = useContext(TranslationContext);
  const gramSelected = coloration === 'gram';

  return (
    <VStack gap={8}>
      <Text fontWeight="bold" fontSize={14}>
        {i18n?.t('page.analysis-details.organism-selection.title')}
      </Text>
      <Text fontSize={12} color={COLORS.NEUTRAL[90]}>
        {i18n?.t('page.analysis-details.organism-selection.subtitle')}
      </Text>
      <HStack gap={10} flexWrap="wrap">
        {ORGANISMS.map(({ key, letter, color }) => {
          const isDisabledByGram = gramSelected && GRAM_ONLY_ORGANISMS.has(key);
          const isSelected = selected.has(key) && !isDisabledByGram;
          return (
            <Pressable
              key={key}
              onPress={() => !isDisabledByGram && onToggle(key)}
              testID={`${testID ?? ''}organism_${key}`}
              style={{
                flex: 1,
                minWidth: 120,
                borderWidth: 2,
                borderColor: isDisabledByGram ? COLORS.NEUTRAL[80] : isSelected ? COLORS.ACCENT[100] : COLORS.NEUTRAL[70],
                borderRadius: 10,
                padding: 10,
                backgroundColor: isDisabledByGram ? COLORS.NEUTRAL[60] : isSelected ? '#FFF5F4' : COLORS.NEUTRAL[0],
                opacity: isDisabledByGram ? 0.5 : 1,
                cursor: isDisabledByGram ? 'not-allowed' : 'pointer',
              }}
            >
              <HStack gap={10} alignItems="center">
                <Box
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 18,
                    backgroundColor: isDisabledByGram ? COLORS.NEUTRAL[80] : color,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text fontWeight="bold" color={COLORS.NEUTRAL[0]} fontSize={16}>
                    {letter}
                  </Text>
                </Box>
                <VStack flex={1}>
                  <Text fontWeight="bold" fontSize={13} color={isDisabledByGram ? COLORS.NEUTRAL[80] : undefined}>
                    {i18n?.t(`page.analysis-details.organism-selection.${key}.label`)}
                  </Text>
                </VStack>
                {isSelected && <CheckIcon size={16} color={COLORS.ACCENT[100]} />}
              </HStack>
            </Pressable>
          );
        })}
      </HStack>
    </VStack>
  );
};
